import { EventBus } from './EventBus'

export class GridStateManager {
  /**
   * @param {object} options
   * @param {string} options.gridName - unique identifier for the grid
   * @param {BaseCollection} options.collection - collection instance
   * @param {boolean} options.initializeOnCreation - whether to initialize state on creation
   */
  constructor(options) {
    this.gridName = options.gridName
    this.collection = options.collection
    this.storageKey = `hasVisited${this.gridName}`
    this.stateStorageKey = `${this.gridName}State`
    this.navigationTrackingKey = `${this.gridName}_lastActive`
    this.hasInitialized = false
    this.isRetrying = false // Add retry flag to prevent infinite loops

    // Set up error listener for this grid
    if (options.listenForErrors !== false) {
      this.setupErrorListener()
    }

    // Initialize the grid state on creation (deferred to next tick)
    if (options.initializeOnCreation) {
      setTimeout(() => {
        if (!this.hasInitialized) {
          this.initializeOnCreation()
        }
      }, 0)
    }
  }

  /**
   * Initialize state on creation (usually in constructor)
   */
  initializeOnCreation() {
    // Set initialized flag to prevent duplicate calls
    this.hasInitialized = true

    // Check for any of our reset conditions
    if (this.shouldResetFilters()) {
      this.resetState()
    }
  }

  /**
   * Check various conditions to determine if filters should be reset
   * - Page refresh
   * - Navigation timeout (returned after some time)
   * - Error history
   */
  shouldResetFilters() {
    // Check navigation timeout
    if (this.isNavigationTimeout()) {
      return true
    }

    // Check if error flag is set for this grid (previous error occurred)
    if (localStorage.getItem(`${this.gridName}_hadError`)) {
      localStorage.removeItem(`${this.gridName}_hadError`)
      return true
    }

    return false
  }

  /**
   * Reset all grid state
   * @param {boolean} emitEvent - Whether to emit the reset event (default: true)
   * @param {boolean} preserveSession - Whether to preserve session storage (default: false)
   */
  resetState(emitEvent = true, preserveSession = false) {
    if (!this.collection) return

    // Log filter state before reset for debugging

    // Store the current filters for debugging/restoration if needed
    if (this.collection.filter || this.collection.orderBy) {
      localStorage.setItem(`${this.gridName}_lastFilter`, this.collection.filter || '')
      localStorage.setItem(`${this.gridName}_lastOrderBy`, this.collection.orderBy || '')
    }

    // Reset collection state
    this.collection.filter = ''
    this.collection.orderBy = ''
    this.collection.page = 1

    // Reset DevExtreme's internal filter state if it exists
    if (this.collection.devExtremeStoreFilterArray) {
      this.collection.devExtremeStoreFilterArray = []
    }

    // Force clearing DevExtreme state in localStorage
    this.clearDevExtremeState()

    // Clear session storage only if not preserving session
    if (!preserveSession) {
      sessionStorage.removeItem(this.storageKey)
    }

    // Clear local storage
    localStorage.removeItem(this.stateStorageKey)

    // Update last activity timestamp
    localStorage.setItem(this.navigationTrackingKey, Date.now().toString())

    // Emit reset event for the grid if requested
    if (emitEvent) {
      this.emitResetEvent()
    }

    return this
  }

  /**
   * Clear DevExtreme UI state stored in localStorage
   * DevExtreme stores its own UI state that needs to be cleared
   */
  clearDevExtremeState() {
    // Look for all DevExtreme related keys for this grid
    const dxKeysToRemove = []

    // DevExtreme stores state in localStorage with keys like "dx-data-grid-123456"
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)

      // Check if this is a DevExtreme key (starts with dx-)
      if (key && key.startsWith('dx-')) {
        try {
          // Parse the stored value to see if it's related to our grid
          const value = JSON.parse(localStorage.getItem(key))

          // If this state contains our grid's filter info or has a matching key
          if (
            (value && value.columns && this.gridName && key.includes(this.gridName)) ||
            (this.collection && this.collection.instanceId && key.includes(this.collection.instanceId))
          ) {
            dxKeysToRemove.push(key)
          }
        } catch (e) {
          // If JSON parsing fails, just check if the key contains our grid name
          if (this.gridName && key.includes(this.gridName)) {
            dxKeysToRemove.push(key)
          }
        }
      }
    }

    // Remove all matching DevExtreme state keys
    dxKeysToRemove.forEach((key) => {
      localStorage.removeItem(key)
    })
  }

  /**
   * Check if the collection is being opened for the first time in this session
   */
  isFirstVisit() {
    return !sessionStorage.getItem(this.storageKey)
  }

  /**
   * Mark as visited in this session
   */
  markAsVisited() {
    sessionStorage.setItem(this.storageKey, 'true')
    return this
  }

  /**
   * Setup listener for error events
   */
  setupErrorListener() {
    // Use a proxy function to handle 'this' context properly
    this._handleErrorProxy = (error) => {
      if (error && error.response && error.response.status >= 400) {
        this.resetState()
      }
    }

    // Add listener to EventBus
    EventBus.$on('apiError', this._handleErrorProxy)
  }

  /**
   * Clean up listeners
   */
  destroy() {
    if (this._handleErrorProxy) {
      EventBus.$off('apiError', this._handleErrorProxy)
    }
  }

  /**
   * Emit reset event for the grid - but don't cause a data refresh
   */
  emitResetEvent() {
    // Modified to preserve filter functionality
    EventBus.$emit('resetGridState', {
      gridName: this.gridName,
      clearFilters: true,
      clearSort: true,
      preserveFiltering: true, // New flag to preserve filter functionality
      preventDataRefresh: true,
    })
    return this
  }

  /**
   * Check if user returned after navigation timeout
   */
  isNavigationTimeout() {
    const lastActiveTimestamp = parseInt(localStorage.getItem(this.navigationTrackingKey) || '0')
    const currentTime = Date.now()
    const timeSinceLastActive = currentTime - lastActiveTimestamp

    // If it's been more than 5 minutes since last using this grid, reset
    return timeSinceLastActive > 5 * 60 * 1000
  }

  /**
   * Check and handle first visit logic
   */
  handleFirstVisit() {
    if (this.isFirstVisit()) {
      // Don't clear filters if we're actively filtering
      // This prevents clearing filters that were just set by setRequestOpts()
      const hasActiveFilter = this.collection.filter && this.collection.filter.length > 0

      if (!hasActiveFilter) {
        this.collection.filter = ''
        this.collection.orderBy = ''
        this.emitResetEvent()
        this.resetState()
      } else {
        // Mark as visited even when preserving filter to prevent future first visit issues
        this.markAsVisited()
      }
      return true
    }
    return false
  }

  /**
   * Complete fetch workflow - call before API request
   */
  beforeFetch() {
    // Set initialized flag
    this.hasInitialized = true

    // Update activity timestamp
    localStorage.setItem(this.navigationTrackingKey, Date.now().toString())

    // Validate filter syntax before proceeding
    if (this.collection.filter && !this.isValidFilterSyntax(this.collection.filter)) {
      console.warn('Invalid filter syntax detected, resetting filters')
      this.resetState()
    }

    // Clear preserved filters whenever there are error flags on first fetch
    // Use hasInitialized flag to detect first fetch after page load/refresh
    const hasErrorFlag = localStorage.getItem(`${this.gridName}_hadError`)
    const isNavigationTimeout = this.isNavigationTimeout()
    const isFirstVisit = this.isFirstVisit()
    const isFirstFetchAfterPageLoad = !this.hasInitialized

    // If first fetch after page load AND we have both error flag and preserved filter, clear it
    // This works on every page refresh because hasInitialized resets on each page load
    if (isFirstFetchAfterPageLoad && hasErrorFlag && this.collection.filter) {
      this.collection.filter = ''
      this.collection.orderBy = ''
      this.clearDevExtremeState()
      this.resetState()
      localStorage.removeItem(`${this.gridName}_hadError`)
      this.markAsVisited()
    }
    // Always reset for navigation timeout
    else if (isNavigationTimeout) {
      this.resetState()
      this.markAsVisited()
    }
    // For error flags, only reset if not actively filtering
    else if (hasErrorFlag) {
      // Only reset if we're not in the middle of an active filtering operation
      if (!this.collection.filter || this.collection.filter.length === 0) {
        localStorage.removeItem(`${this.gridName}_hadError`) // Clear flag when resetting
        this.resetState()
      } else {
        // Clear the error flag immediately when actively filtering
        // This prevents the flag from interfering with subsequent filter operations
        localStorage.removeItem(`${this.gridName}_hadError`)
      }
    }
    // Handle first visit - this will handle page refresh naturally
    else if (isFirstVisit) {
      this.handleFirstVisit()
    }

    // Set initialized flag AFTER all the clearing logic has run
    this.hasInitialized = true

    return this
  }

  /**
   * Validate filter syntax to prevent malformed requests
   * @param {string} filterString - The filter string to validate
   * @returns {boolean} Whether the filter appears valid
   */
  isValidFilterSyntax(filterString) {
    if (!filterString) return true

    // Basic checks for common syntax issues
    // Unbalanced operators, incomplete expressions, etc.

    // Check for incomplete expressions (column=)
    if (/=[^*\w]/.test(filterString) || filterString.endsWith('=')) {
      return false
    }

    // Check for malformed patterns
    const invalidPatterns = [
      /,,/, // Double commas
      /\|\|/, // Double pipes
      /=\s*$/, // Ends with equals sign
      /=\s*,/, // Equals followed by comma
      /=\s*\|/, // Equals followed by pipe
    ]

    return !invalidPatterns.some((pattern) => pattern.test(filterString))
  }

  /**
   * Complete fetch workflow - call after successful API request
   */
  afterFetch() {
    // Mark as visited
    this.markAsVisited()

    // Clear retry flag on successful fetch
    this.isRetrying = false

    // Clear error flag on successful fetch
    localStorage.removeItem(`${this.gridName}_hadError`)

    // Emit focus restoration event if filtering
    if (this.collection.filter) {
      EventBus.$emit('restoreFocusEvent')
    }

    return this
  }

  /**
   * Handle errors in fetch
   */
  handleFetchError(error) {
    // Mark as visited on error to prevent first visit issues
    this.markAsVisited()

    // Set error flag to ensure reset runs on next visit
    localStorage.setItem(`${this.gridName}_hadError`, 'true')

    // Store the filters that caused the error (for debugging)
    if (this.collection.filter || this.collection.orderBy) {
      localStorage.setItem(`${this.gridName}_errorFilter`, this.collection.filter || '')
      localStorage.setItem(`${this.gridName}_errorOrderBy`, this.collection.orderBy || '')
    }

    // Clear the grid data but don't immediately reset filters
    // (only when they return to this view later)
    if (this.collection) {
      this.collection.items = []
      this.collection.count = 0
    }

    // Only reset state for certain errors like 400 (bad request) that indicate filter issues
    if (error.response?.status === 400 || error.response?.status === 500) {
      this.emitResetEvent()

      // Preserve session storage if we're actively filtering to prevent alternating behavior
      const isActivelyFiltering = this.collection.filter && this.collection.filter.length > 0
      this.resetState(true, isActivelyFiltering)
    }

    // Create error message
    const errorMessage = error.response?.status
      ? `Request failed with status code ${error.response.status}`
      : error.message || 'Request failed'

    // Emit error event for the grid to display
    EventBus.$emit('gridDisplayError', {
      gridName: this.gridName,
      message: errorMessage,
      error: error,
      clearData: true,
    })

    return error
  }

  /**
   * Get the current filter values from the grid
   * @param {Object} grid - The DevExtreme grid instance
   * @returns {Object} Object with column names as keys and filter values as values
   */
  getFilterTexts(grid) {
    // Get from grid directly if provided
    if (grid) {
      const result = this.extractFilterTextsFromGrid(grid)
      return result
    }

    // Try to get from collection's stored filter array
    if (this.collection && this.collection.devExtremeStoreFilterArray) {
      const result = this.extractFilterTextsFromArray(this.collection.devExtremeStoreFilterArray)
      return result
    }

    return {}
  }

  /**
   * Extract filter texts directly from DevExtreme grid
   * @param {Object} grid - The DevExtreme grid instance
   * @returns {Object} Object with column names as keys and filter values as values
   */
  extractFilterTextsFromGrid(grid) {
    const result = {}

    // Get columns with their filter values
    const columns = grid.getVisibleColumns()

    columns.forEach((column) => {
      if (column.filterValue !== undefined) {
        result[column.dataField] = {
          text: column.filterValue,
          operator: column.selectedFilterOperation || '=',
          displayValue: column.filterValue, // For display purposes
        }
      }
    })

    return result
  }

  /**
   * Extract filter texts from stored filter array
   * @param {Array} filterArray - The DevExtreme filter array
   * @returns {Object} Object with column names as keys and filter values as values
   */
  extractFilterTextsFromArray(filterArray) {
    const result = {}

    if (!Array.isArray(filterArray)) {
      return result
    }

    // Process flat filter array [column, operator, value]
    if (filterArray.length === 3 && typeof filterArray[0] === 'string') {
      const [column, operator, value] = filterArray
      result[column] = {
        text: value,
        operator: operator,
        displayValue: value,
      }
      return result
    }

    // Process complex filter array
    this.processFilterArrayRecursive(filterArray, result)

    return result
  }

  /**
   * Recursively process filter array to extract values
   * @param {Array} filterArray - The filter array or sub-array
   * @param {Object} result - The result object to populate
   */
  processFilterArrayRecursive(filterArray, result) {
    if (!Array.isArray(filterArray)) {
      return
    }

    filterArray.forEach((item) => {
      if (Array.isArray(item) && item.length === 3 && typeof item[0] === 'string') {
        // This is a basic filter [column, operator, value]
        const [column, operator, value] = item
        result[column] = {
          text: value,
          operator: operator,
          displayValue: value,
        }
      } else if (Array.isArray(item)) {
        // This is a nested filter
        this.processFilterArrayRecursive(item, result)
      }
    })
  }

  /**
   * Get sorting information
   * @param {Object} grid - The DevExtreme grid instance
   * @returns {Object} Object with sorting information
   */
  getSortInfo(grid) {
    if (!grid) {
      // Try to get from collection
      if (this.collection && this.collection.orderBy) {
        const orderByParts = this.collection.orderBy.split(' ')
        return {
          column: orderByParts[0],
          direction: orderByParts[1] || 'asc',
        }
      }
      return { column: null, direction: null }
    }

    // Get from grid
    const sortInfo = grid.option('sorting')
    if (sortInfo && sortInfo.length > 0) {
      return {
        column: sortInfo[0].selector,
        direction: sortInfo[0].desc ? 'desc' : 'asc',
      }
    }

    return { column: null, direction: null }
  }

  /**
   * Called when leaving the view/component with this grid
   * Use in component's beforeDestroy hook
   */
  onLeaveView() {
    localStorage.setItem(this.navigationTrackingKey, Date.now().toString())
  }
}
